import classNames from 'classnames';
import { registredSymbolToSup } from 'modules/Abbreviations/utils/abbreviationsListToHTML';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { rootDocument as rootDocumentSelector } from 'containers/RootDocument/selectors';
import { TextAbbreviationDocument, TextAbbreviationDocumentsArray } from 'models';
import { createAbbreviation, getAbbreviations } from '../../../api';
import { setTextAbbreviations } from '../../../store/actions';
import styles from '../styles.module.scss';
import NewAbbreviation from './NewAbbreviation';

type Props = {
  term: string;
  abbreviationDocument?: TextAbbreviationDocument;
  abbreviationsList: TextAbbreviationDocumentsArray;
  onSelect: (abbreviationDocumentId: TextAbbreviationDocument['id']) => void;
  className?: string;
  onDefinitionCancel?: () => void;
  dataTestId?: string;
};

const AbbreviationsList: React.FunctionComponent<Props> = (props: Props) => {
  const {
    className: abbreviationListClassName,
    term,
    abbreviationDocument,
    abbreviationsList,
    onSelect,
    onDefinitionCancel,
    dataTestId = 'abbreviationsList',
  } = props;
  const rootDocument = useSelector(rootDocumentSelector);
  const dispatch = useDispatch();

  const addAbbreviation = useCallback(async (_definition: string): Promise<TextAbbreviationDocument['id']> => {
    const rootDocumentId = rootDocument.get('documentId');

    const createdAbbreviationId = await createAbbreviation({
      rootDocumentId,
      abbreviation: {
        term,
        definition: _definition,
      },
    });

    const textAbbreviationDocuments = await getAbbreviations(rootDocumentId);

    dispatch(setTextAbbreviations(textAbbreviationDocuments));

    return createdAbbreviationId;
  }, [dispatch, rootDocument, term]);

  const onAddAbbreviation = useCallback(
    (newDefinition: string): Promise<void> => addAbbreviation(newDefinition).then(onSelect),
    [addAbbreviation, onSelect],
  );

  const existingDefinitions = abbreviationsList.map(({ abbreviation }) => abbreviation.definition.toLowerCase());

  if (!abbreviationsList) {
    return (
      <div className={abbreviationListClassName} data-testid={`${dataTestId}-emptyList`}>
        <NewAbbreviation onAdd={onAddAbbreviation} existingDefinitions={existingDefinitions} onCancel={onDefinitionCancel}
          dataTestId={`${dataTestId}-emptyList-newAbbreviation`}/>
      </div>
    );
  }

  return (
    <div className={abbreviationListClassName} data-testid={dataTestId}>
      <div className={classNames(styles.abbreviationsList)}>
        {abbreviationsList.map((_abbreviationDocument, index) => (
          <button
            key={_abbreviationDocument.id}
            className={classNames({ selected: _abbreviationDocument.id === abbreviationDocument?.id })}
            onClick={(): void => onSelect(_abbreviationDocument.id)}
            dangerouslySetInnerHTML={{ __html: registredSymbolToSup(_abbreviationDocument.abbreviation.definition) }}
            data-testid={`${dataTestId}-item${index}`}
            data-vault-document-id={_abbreviationDocument.documentId}
          />
        ))}
      </div>
      <NewAbbreviation onAdd={onAddAbbreviation} existingDefinitions={existingDefinitions} onCancel={onDefinitionCancel}
        dataTestId={`${dataTestId}-newAbbreviation`}/>
    </div>
  );
};

AbbreviationsList.defaultProps = {
  className: undefined,
};

export default AbbreviationsList;
